import {
	isQuestionEffort,
	type QuestionEffort,
} from "@ui/organisms/QuestionBox/questionEfforts";
import type { AskFailure } from "./askFailure";
import type { ChatPanelController } from "./chat-panel/ChatPanel";
import type { CollectedMentions } from "./chat-panel/mentionCollector";

/**
 * What `askFollowUp` resolves to, for the app to present natively.
 *
 * On a thread page a follow-up is a question of its own, and `id` and `path`
 * name it. On a question page it is another turn of the same conversation, so
 * they name that question, and `interactionId` names the turn.
 */
export type MobileAskResult =
	| { ok: true; id: string; path: string; interactionId?: string }
	| AskFailure;

/**
 * What every `window.view` offers a mobile app driving a hosted chat page.
 * Video evidence is addressed by interaction id alone, wherever on the page
 * it is.
 */
type MobileViewBase = {
	/** `effort` is the one the app's user picked; omitted or null for auto. */
	askFollowUp(
		question: string,
		effort?: string | null,
	): Promise<MobileAskResult>;
	/**
	 * `askFollowUp` for apps that can't await a promise (Android's
	 * `evaluateJavascript` doesn't): the result arrives as an
	 * `askfollowupresult` event on `document`, as `{ requestId, result }`.
	 * Every call reports exactly once, even if asking throws.
	 *
	 * `requestId` is chosen by the app, unique per call, and only echoed back.
	 * It is how the app tells which call a result belongs to: calls can
	 * overlap, and their results don't arrive in the order they were made. A
	 * second ask refused as `answer_in_progress` reports straight away, before
	 * the first one's result.
	 */
	askFollowUpWithCallback(
		requestId: string,
		question: string,
		effort?: string | null,
	): void;
	registerForChatEvents(events: string[]): void;
	scrollVideoEvidenceOffView(transactionID: string, offset: number): boolean;
	hideVideoEvidence(transactionID: string): void;
	showVideoEvidence(transactionID: string): void;
	getMentions(): CollectedMentions;
};

/** A chat page: `ask` sends a follow-up and reports nothing back. */
export type MobileChatView = MobileViewBase & {
	ask(question: string): void;
};

/** A creator thread page. */
export type MobileThreadView = MobileViewBase & {
	/** The same as `askFollowUp`. Kept until the apps call that instead. */
	ask(question: string, effort?: string | null): Promise<MobileAskResult>;
};

export type MobileView = MobileChatView | MobileThreadView;

/** The effort an app asked at, undefined for auto, or a refusal if it's unknown. */
export function appEffort(
	effort: string | null | undefined,
): { ok: true; effort?: QuestionEffort } | AskFailure {
	if (effort == null) {
		return { ok: true };
	}
	return isQuestionEffort(effort)
		? { ok: true, effort }
		: { ok: false, reason: "invalid_effort" };
}

/** Tells the app how the ask it named `requestId` went, once it has. */
export function dispatchAskFollowUpResult(
	requestId: string,
	asking: Promise<MobileAskResult>,
) {
	// The app is waiting on this event, so an ask that throws must still report.
	void asking
		.catch((error: unknown): MobileAskResult => {
			console.error("askFollowUp failed:", error);
			return { ok: false };
		})
		.then((result) => {
			document.dispatchEvent(
				new CustomEvent("askfollowupresult", { detail: { requestId, result } }),
			);
		});
}

declare global {
	interface Window {
		view: MobileView | null;
	}
}

/**
 * Enable a mobile app to interact with the chat panel.
 */
export class MobileChatInterface implements MobileChatView {
	controller: ChatPanelController;
	private readonly questionId: string;

	constructor(controller: ChatPanelController, questionId: string) {
		this.controller = controller;
		this.questionId = questionId;
	}

	ask(question: string) {
		this.controller.askQuestion(question);
	}

	async askFollowUp(
		question: string,
		effort?: string | null,
	): Promise<MobileAskResult> {
		const chosen = appEffort(effort);
		if (chosen.ok === false) {
			return chosen;
		}
		const result = await this.controller.askFollowUp(question, chosen.effort);
		if (result.ok === false) {
			return result;
		}
		return {
			ok: true,
			id: this.questionId,
			path: window.location.pathname,
			interactionId: result.interactionId,
		};
	}

	askFollowUpWithCallback(
		requestId: string,
		question: string,
		effort?: string | null,
	) {
		dispatchAskFollowUpResult(requestId, this.askFollowUp(question, effort));
	}

	registerForChatEvents(events: string[]) {
		this.controller.registerForChatEvents(events);
	}

	scrollVideoEvidenceOffView(transactionID: string, offset: number) {
		return this.controller.scrollVideoEvidenceOffView(transactionID, offset);
	}

	hideVideoEvidence(transactionID: string) {
		this.controller.hideVideoEvidence(transactionID);
	}

	showVideoEvidence(transactionID: string) {
		this.controller.showVideoEvidence(transactionID);
	}

	/**
	 * Every account, influencer and clip mentioned in the conversation so far,
	 * grouped by the stream event that carried it. `video_mention` covers both
	 * the clips cited in an answer and the sources behind it, the latter folded
	 * onto the same shape (see mentionCollector).
	 */
	getMentions() {
		return this.controller.getMentions();
	}
}
